import { ProductType } from '../../__generated__/graphql';
import type { BrandId } from './brands/types';

export interface SiteConfig {
  defaultProductType: ProductType;
  isSingleProductSite: boolean;
  siteLabel: string;
  siteName: string;
  siteLogoType?: string;
  allowedProductTypes?: ProductType[];
  /** Visual identity and legal content; defaults to `autopilotee` (see `constants/brands`). */
  brandId?: BrandId;
}

/**
 * Host of the stays site (spec 016). A placeholder until the domain is decided
 * (D1); it must match the backend `brands.brandSite` of the stays brand.
 * Preview locally with `yarn start:stays:dev`.
 */
export const STAYS_SITE_HOST = 'stays.autopilotee.com';

const STAYS_SITE_CONFIG: SiteConfig = {
  defaultProductType: ProductType.House,
  isSingleProductSite: true,
  siteLabel: 'stay',
  siteName: 'Autopilotee Stays',
  allowedProductTypes: [ProductType.House],
  brandId: 'stays',
};

/**
 * Host of the services site (spec 019): cleaning and handover jobs booked on a
 * worker. A placeholder until the domain is decided; it must match the backend
 * `brands.brandSite` of the services brand. Preview locally with
 * `yarn start:services:dev` (port 3100, the brand row's `localhost:3100` alias).
 */
export const SERVICES_SITE_HOST = 'services.autopilotee.com';

const SERVICES_SITE_CONFIG: SiteConfig = {
  defaultProductType: ProductType.HouseAndVehicleService,
  isSingleProductSite: true,
  siteLabel: 'service',
  siteName: 'Autopilotee Services',
  allowedProductTypes: [ProductType.HouseAndVehicleService],
  brandId: 'services',
};

const SITE_CONFIGS: Record<string, SiteConfig> = {
  [STAYS_SITE_HOST]: STAYS_SITE_CONFIG,
  [SERVICES_SITE_HOST]: SERVICES_SITE_CONFIG,
  [`www.${SERVICES_SITE_HOST}`]: SERVICES_SITE_CONFIG,
  [`staging.${SERVICES_SITE_HOST}`]: SERVICES_SITE_CONFIG,
  // Public alias of the services brand (backend brands.aliases); must stay on
  // SERVICES_SITE_CONFIG or getSiteConfig() falls back to cars.
  'housekeeping-testing.autopilotee.com': SERVICES_SITE_CONFIG,
  [`www.${STAYS_SITE_HOST}`]: STAYS_SITE_CONFIG,
  [`staging.${STAYS_SITE_HOST}`]: STAYS_SITE_CONFIG,
  // Public alias of the stays brand (backend brands.aliases); must stay on STAYS_SITE_CONFIG
  // or getSiteConfig() falls back to cars and house UI disappears.
  'stay-testing.autopilotee.com': STAYS_SITE_CONFIG,
  'autopilotee.com': {
    defaultProductType: ProductType.Car,
    isSingleProductSite: true,
    siteLabel: 'car',
    siteName: 'AutoPilotee Cars',
  },
  'www.autopilotee.com': {
    defaultProductType: ProductType.Car,
    isSingleProductSite: true,
    siteLabel: 'car',
    siteName: 'AutoPilotee Cars',
  },
  localhost: {
    defaultProductType: ProductType.Car,
    isSingleProductSite: true,
    siteLabel: 'car',
    siteName: 'AutoPilotee Cars',
  },
  'lenvoy.co': {
    defaultProductType: ProductType.Car,
    isSingleProductSite: true,
    siteLabel: 'car',
    siteName: 'Lenvoy Car Rental',
    brandId: 'lenvoy',
  },
  'www.lenvoy.co': {
    defaultProductType: ProductType.Car,
    isSingleProductSite: true,
    siteLabel: 'car',
    siteName: 'Lenvoy Car Rental',
    brandId: 'lenvoy',
  },
  'staging.lenvoy.co': {
    defaultProductType: ProductType.Car,
    isSingleProductSite: true,
    siteLabel: 'car',
    siteName: 'Lenvoy Car Rental',
    brandId: 'lenvoy',
  },
  // Interim staging host for the Lenvoy brand until staging.lenvoy.co exists.
  'staging.handysurrogate.com': {
    defaultProductType: ProductType.Car,
    isSingleProductSite: true,
    siteLabel: 'car',
    siteName: 'Lenvoy Car Rental',
    brandId: 'lenvoy',
  },
  'instasharings.com': {
    defaultProductType: ProductType.Pickleball,
    isSingleProductSite: false,
    siteLabel: 'sports gear',
    siteName: 'InstaShareings',
    siteLogoType: 'instasharings',
    allowedProductTypes: [
      ProductType.Pickleball,
      ProductType.Tennis,
      ProductType.Snowboarding,
      ProductType.Sports,
    ],
  },
  'www.instasharings.com': {
    defaultProductType: ProductType.Pickleball,
    isSingleProductSite: false,
    siteLabel: 'sports gear',
    siteName: 'InstaShareings',
    siteLogoType: 'instasharings',
    allowedProductTypes: [
      ProductType.Pickleball,
      ProductType.Tennis,
      ProductType.Snowboarding,
      ProductType.Sports,
    ],
  },
};

const DEFAULT_SITE_CONFIG: SiteConfig = {
  defaultProductType: ProductType.Car,
  isSingleProductSite: true,
  siteLabel: 'car',
  siteName: 'Autopilotee Cars',
};

export function getSiteConfig(): SiteConfig {
  const hostnameOverride = process.env.REACT_APP_SITE_HOSTNAME;
  const hostname = hostnameOverride || window.location.hostname;
  return SITE_CONFIGS[hostname] || DEFAULT_SITE_CONFIG;
}

/** What this site sells, as the kinds the UI branches on (spec 016, spec 019). */
export type SiteProductKind = 'car' | 'stay' | 'service';

export function productKindOf(type: ProductType | null | undefined): SiteProductKind {
  if (type === ProductType.House) return 'stay';
  if (type === ProductType.HouseAndVehicleService) return 'service';
  return 'car';
}

/**
 * `stay` on the stays site, `service` on the services site, `car` everywhere
 * else (including multi-product sites).
 */
export function getSiteProductKind(): SiteProductKind {
  return productKindOf(getSiteConfig().defaultProductType);
}

export function isStaysSite(): boolean {
  return getSiteProductKind() === 'stay';
}

/** The services site (spec 019): Providers, workers and jobs. */
export function isServicesSite(): boolean {
  return getSiteProductKind() === 'service';
}
